import React from 'react';
import { motion } from 'framer-motion';
import { blogPosts } from '../../constants/newsData';

const FadeIn = ({ children, className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const News = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1a09]">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold tracking-[0.2em] uppercase text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800/50 rounded-full bg-brand-50 dark:bg-brand-900/20"
          >
            Press & Updates
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl md:text-6xl font-display text-gray-900 dark:text-white mb-6"
          >
            Latest <span className="text-gradient">News</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
          >
            Stay updated with the latest press coverage, research, and developments from Budbreak.
          </motion.p>
        </div>
      </section>

      {/* Articles grid */}
      <section className="pb-24 md:pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Featured article (first one) */}
          {blogPosts.length > 0 && (
            <FadeIn className="mb-12">
              <a
                href={blogPosts[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-2xl overflow-hidden bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 card-lift"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                  <div className="relative h-64 md:h-auto overflow-hidden">
                    <img
                      src={blogPosts[0].image}
                      alt={blogPosts[0].title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10 md:bg-gradient-to-l md:from-transparent md:to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-500 text-white">
                        {blogPosts[0].category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <div className="text-xs text-gray-500 dark:text-gray-500 mb-3 font-medium tracking-wide uppercase">
                      {blogPosts[0].date}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display text-gray-900 dark:text-white mb-4 leading-snug">
                      {blogPosts[0].title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {blogPosts[0].excerpt}
                    </p>
                    <span className="inline-flex items-center text-brand-600 dark:text-brand-400 font-medium text-sm group-hover:text-brand-700 dark:group-hover:text-brand-300 transition-colors">
                      Read Full Article
                      <svg className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            </FadeIn>
          )}

          {/* Remaining articles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.slice(1).map((post, i) => (
              <FadeIn key={post.id} delay={i * 0.1}>
                <div className="group rounded-2xl overflow-hidden bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 card-lift h-full flex flex-col">
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-500 text-white">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="text-xs text-gray-500 dark:text-gray-500 mb-3 font-medium tracking-wide uppercase">
                      {post.date}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2 font-sans leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>
                    {post.url ? (
                      <a
                        href={post.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-brand-600 dark:text-brand-400 font-medium text-sm hover:text-brand-700 dark:hover:text-brand-300 transition-colors group/link"
                      >
                        Read Article
                        <svg className="w-4 h-4 ml-1 transition-transform group-hover/link:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>
                    ) : (
                      <span className="text-brand-600 dark:text-brand-400 font-medium text-sm">
                        Read More →
                      </span>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;